<template>
  <div class="flex h-full">
    <div class="bullet-form overflow-auto hover:overflow-visible px-5 py-5 flex flex-col justify-between">
      <div>
        <tabs
          small
          :tabs="tabs.filter(el => el.valid)"
          :current="currentTab"
          @changed="(tab) => (currentTab = tab)"
          class="mt-4"
        />
        <resume-form
          :key="currentTab"
          :form="currentTab"
          class="mt-5"
          :default-values="defaultValues"
          :is_resume_builder="true"
          @resume:loading="handleLoading"
          @resume:received="handleReceived"
        />
      </div>

      <div class="space-y-0.5">
        <pdf-feature-usage-left v-if="isSubscriptionModelActivated() && userFeatureUsage.length > 0 && !isSubscribe" :collapsed="collapsed" />
        <pdf-tokens-left v-if="!isSubscriptionModelActivated()" />
        <pdf-upgrade-button-sidebar v-else class="block" />
      </div>
    </div>

    <div class="bullet-results w-full h-full z-10">
      <div class="flex items-center justify-between px-5 py-5">
        <div
          v-if="!loading && !!resultsData?.length"
          class="text-base font-medium won-blue"
        >
          Generated Bullet Points
        </div>
        <div v-else />

        <svg
          @click="handleClose"
          class="w-5 h-5 cursor-pointer"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 6L18 18M6 18L18 6L6 18Z"
            stroke="#96AAB7"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <div
        v-if="loading"
        class="flex flex-col items-center justify-center z-20"
        style="color: #505d68; height: 70vh"
      >
        <bullet-loading class="mb-4 spin" />

        <div class="text-xl">Generating results...</div>

        <div class="text-center mt-3 text-sm">
          Hold tight! Your results will
          <br />
          be ready in a couple seconds.
        </div>
      </div>

      <div
        v-if="!loading && !resultsData?.length"
        class="flex flex-col items-center justify-center"
        style="color: #505d68; height: 70vh"
      >
        <div class="text-xl">No results yet!</div>

        <div class="text-center mt-3 text-sm">
          To see results, fill in the
          <br />
          required fields and hit
          <br />
          ‘Generate.’
        </div>
      </div>

      <div v-if="!loading && !!resultsData?.length" class="px-5">
        <div
          v-for="item of resultsData"
          :key="item.id"
          class="flex bullet-item-result"
        >
          <added
            v-if="addedIds[`${section}-${index}`]?.includes(item.id)"
            class="mr-2.5 mt-px cursor-pointer"
            style="min-width: 20px"

          />
          <add-icon
            v-else
            class="mr-2.5 mt-px cursor-pointer"
            style="min-width: 20px"
            @click="() => handleAdd(item)"
          />

          <div class="text-sm won-blue">{{ item.completion }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'

import { decodeHtml, isSubscriptionModelActivated } from '../../utils/helpers'

import ResumeForm from '../../forms/resume-form'
import Tabs from '../tabs'

import BulletLoading from '../../icons/bullet-loading'
import AddIcon from '../../icons/add'
import Added from '../../icons/added'

import PdfTokensLeft from './pdf-tokens-left'
import PdfUpgradeButtonSidebar from './pdf-upgrade-button-sidebar'

import { useBuilder } from '../../store/builder.store'
import { useBulletGenerator } from '../../store/bullet-store'
import { useOnboardingStore } from '../../store/onboarding.store'
import { useSubscriptionPlanStore } from '../../store/subscription.store'
import { useFeatureUsageStore } from '../../store/feature-usage.store'

import { useTiptapStore } from '../../store/tiptap.store'
import { extractContent } from '../../utils/helpers'

import { resumeResultsMock }  from '../../utils/mock'
import PdfFeatureUsageLeft from "./pdf-feature-usage-left.vue";

export default {
  components: {PdfFeatureUsageLeft, ResumeForm, Tabs, BulletLoading, AddIcon, Added, PdfTokensLeft, PdfUpgradeButtonSidebar },

  setup() {
    const addedIds = ref({})
    const loading = ref(false)
    const results = ref(null)

    const storageKey = 'WONSULTING-resume-selected-tab'
    const fromStorage = localStorage.getItem(storageKey)

    const currentTab = ref(fromStorage || 'resume-rewrite-form')

    const builder = useBuilder()
    const bulletGenerator = useBulletGenerator()
    const { section, index } = storeToRefs(bulletGenerator)

    const tiptapStore = useTiptapStore()
    const { editor } = storeToRefs(tiptapStore)

    const onboardingStore = useOnboardingStore()
    const { isOnboarding } = storeToRefs(onboardingStore)
    
    const subscriptionPlanStore = useSubscriptionPlanStore()
    const { isSubscribe } = storeToRefs(subscriptionPlanStore)
    
    const userFeatureUsageStore = useFeatureUsageStore()
    const { userFeatureUsage } = storeToRefs(userFeatureUsageStore)

    const defaultValues = computed(() => {
        // if onboarding show different values
        const defaultRole = isOnboarding.value ? 'Designer' : (bulletGenerator?.title ? decodeHtml(extractContent(bulletGenerator?.title)) : null)
        const defaultWorkExperience = isOnboarding.value ? 'designed a mobile app to help customers retrieve their returned items from a store' : null

        return {
          role: defaultRole,
          current_points: defaultWorkExperience,
        }
    })

    const resultsData = computed(() => {
      if(isOnboarding.value) return resumeResultsMock

      const data = (results.value?.completion || '')
        .split('\n')
        .filter(Boolean)
        .map((e) => e.substr(/[a-z]/i.exec(e)?.index))

        return data.map((e, i) => ({
          completion: e,
          id: uuid(),
        }))
    })

    onMounted(() => {
      document.addEventListener('keydown', e => {
        if(isOnboarding.value) return
        if(e.key == 'Escape') bulletGenerator.reset()
      })
    })

    return {
      editor,
      addedIds,
      currentTab,
      loading,
      results,

      section,
      index,
      defaultValues,
      resultsData,

      builder,
      bulletGenerator,
      isSubscriptionModelActivated,

      storageKey,
      
      isSubscribe,
      userFeatureUsage
    }
  },

  data: () => ({
    tabs: [
      { name: 'Create new points', id: 'resume-form', valid: false },
      { name: 'Create Bullet Points', id: 'resume-rewrite-form', valid: true },
    ],
  }),

  watch: {
    currentTab() {
      this.results = []
      this.loading = false

      localStorage.setItem(this.storageKey, this.currentTab)
    },
  },

  methods: {
    handleLoading(loading) {
      this.loading = loading

      if (loading) {
        this.results = []
      }
    },

    handleReceived(data) {
      this.results = data
    },
      
    createListWith(content) {
        return {
            type: 'bulletList',
            content: [{
                type: 'listItem',
                content: [
                    {
                        "type":"paragraph",
                        "content":[
                            {
                                "type":"text",
                                "text": content
                            }
                        ]
                    }
                ]
            }]
        }
    },

    handleAdd({ id, completion }) {
      const key = `${this.section}-${this.index}`

      if (!this.addedIds?.[key]?.length) {
        this.addedIds[key] = []
      }

      this.addedIds[key].push(id);
      
      const editorContentJSON = this.editor.getJSON();
      const bulletListIndex = editorContentJSON.content.findLastIndex(contentPiece => {
          return contentPiece.type === 'bulletList'
      })
        
        let newContent = editorContentJSON;
      
        if(bulletListIndex !== -1) {
            const bulletList = editorContentJSON.content[bulletListIndex]
            
            bulletList.content.push({
                type: 'listItem',
                content: [
                    {
                        "type":"paragraph",
                        "content":[
                            {
                                "type":"text",
                                "text": completion
                            }
                        ]
                    }
                ]
            })

            newContent.content.splice(bulletListIndex, 1, bulletList)
        }else{
            newContent.content = [...newContent.content, this.createListWith(completion)]
        }

        if(this.editor.isEmpty) {
            const cleanContent = {
                ...newContent,
                content: newContent.content.filter(item => item.type !== 'paragraph')
            }
            
            this.editor.chain().focus().setContent(cleanContent, true).run();
            //trigger blur to save info and focus again
            this.editor.commands.blur();
            this.editor.commands.focus();
        }else{
          this.editor.chain().focus().setContent(newContent, true).run();
          //trigger blur to save info and focus again
          this.editor.commands.blur();
          this.editor.commands.focus();
        }
    },

    handleClose() {
      this.editor.commands.blur();
      this.bulletGenerator.reset();
    },
  },
}
</script>

<style scoped>
.bullet-form {
  width: 326px;
  border-right: 1px solid #96aab7;
}

.bullet-results {
  width: 289px;
  z-index: 10;
}

.bullet-item-result:not(:last-of-type) {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #dbdbdc;
}
</style>
